import React, { useState } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Alert,
} from '@cloudscape-design/components';
import { ExportValidation, SPACE_FILTER_REGEX } from './utils/ExportValidation';
import { createOpenPOExport, CreateExportInput } from 'src/api/hooks/createOpenPOExport';
import { useFlashbar } from 'src/common/hooks/useFlashbar';

interface OpenPOExportModalProps {
  controlId: string;
  activeFilters: Record<string, string[]>;
  onClose: () => void;
  onExportCreated: () => void;
}

export const OpenPOExportModal: React.FC<OpenPOExportModalProps> = ({
  controlId,
  activeFilters,
  onClose,
  onExportCreated,
}) => {
  const [exportName, setExportName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { addFlashbarMessage } = useFlashbar();

  const handleExportNameChange = (value: string) => {
    const filteredValue = value.replace(SPACE_FILTER_REGEX, '');
    setExportName(filteredValue);
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleCreateExport = async () => {
    const error = ExportValidation.validateExportName(exportName);
    if (error) {
      setValidationError(error);
      return;
    }

    setIsLoading(true);
    setValidationError(null);

    try {
      // Convert activeFilters from Record<string, string[]> to Record<string, string[]>
      // Keep all selected values for each filter
      const apiFilters = Object.entries(activeFilters).reduce(
        (acc, [key, values]) => {
          if (values && values.length > 0) {
            acc[key] = values; // Keep all selected values
          }
          return acc;
        },
        {} as Record<string, string[]>
      );

      await createOpenPOExport({
        controlId,
        exportName: exportName.trim(),
        filters: apiFilters,
      });

      addFlashbarMessage({
        type: 'success',
        header: 'Export Created',
        content: `Export "${exportName}" has been created successfully.`,
        dismissible: true,
        id: 'export_success',
      });

      onExportCreated();
      onClose();
      setExportName('');
    } catch (error: any) {
      console.error('Error creating export:', error);

      addFlashbarMessage({
        type: 'error',
        header: 'Export Failed',
        content: 'Failed to create export. Please try again.',
        dismissible: true,
        id: 'export_error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setExportName('');
    setValidationError(null);
    onClose();
  };

  return (
    <Modal
      onDismiss={handleClose}
      visible={true}
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateExport}
              loading={isLoading}
              disabled={isLoading}
            >
              Create Export
            </Button>
          </SpaceBetween>
        </Box>
      }
      header="Create Open PO Export"
    >
      <SpaceBetween size="m">
        <Alert type="info">
          <strong>Export Name Guidelines:</strong>
          <ul style={{ marginTop: '8px', marginBottom: '0' }}>
            <li>Use only letters, numbers, underscores (_), hyphens (-), and periods (.)</li>
            <li>Must be between 1 and 100 characters</li>
            <li>Each export name must be unique for your account</li>
          </ul>
        </Alert>

        <FormField
          label="Export Name"
          description="Enter a unique name for your export"
          errorText={validationError}
        >
          <Input
            value={exportName}
            onChange={({ detail }) => handleExportNameChange(detail.value)}
            placeholder="e.g., monthly_report_2024"
            disabled={isLoading}
          />
        </FormField>

        <FormField label="Control ID" description="The control ID for this export">
          <Input value={controlId} disabled={true} />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
